import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { setRequestTrackingAuthToken } from '../features/request-tracking';
import { setTransactionServerAuthToken } from '../api/schedulingApis';
import {
  getAuthToken,
  getPortalProjectImage,
  getPortalProjectList,
  getProjectId,
  switchPortalProject,
  type PortalProjectOption,
} from '../utils/cookies';
import { getProjectDisplayName } from '../utils/projectDisplay';
import { handlePortalScopeChange } from '../utils/portalScopeChange';

export interface PortalProjectContextValue {
  projectId: string;
  projectName: string;
  projectImage: string;
  projectOptions: PortalProjectOption[];
  canSwitchProject: boolean;
  switching: boolean;
  switchProject: (projectId: string) => Promise<boolean>;
}

const PortalProjectContext = createContext<PortalProjectContextValue | null>(null);

export function PortalProjectProvider({ children }: { children: ReactNode }) {
  const [projectId, setProjectId] = useState(() => getProjectId());
  const [projectName, setProjectName] = useState(() => getProjectDisplayName());
  const [switching, setSwitching] = useState(false);

  const projectOptions = useMemo(() => getPortalProjectList(), [projectId]);
  const projectImage = useMemo(
    () =>
      projectOptions.find((p) => p.id === projectId)?.projectImage ??
      getPortalProjectImage(projectId),
    [projectId, projectOptions],
  );
  const canSwitchProject = projectOptions.length > 1;

  const switchProject = useCallback(async (nextProjectId: string) => {
    const result = switchPortalProject(nextProjectId);
    if (!result.success || !result.snapshot) {
      return false;
    }

    setSwitching(true);
    try {
      if (result.scopeChanged) {
        await handlePortalScopeChange(result.previousProjectId);
      }

      const token = getAuthToken();
      setTransactionServerAuthToken(token);
      setRequestTrackingAuthToken(token);

      setProjectId(result.snapshot.projectId);
      setProjectName(result.snapshot.projectName);
      return true;
    } finally {
      setSwitching(false);
    }
  }, []);

  const value = useMemo<PortalProjectContextValue>(
    () => ({
      projectId,
      projectName,
      projectImage,
      projectOptions,
      canSwitchProject,
      switching,
      switchProject,
    }),
    [projectId, projectName, projectImage, projectOptions, canSwitchProject, switching, switchProject],
  );

  return (
    <PortalProjectContext.Provider value={value}>
      {children}
    </PortalProjectContext.Provider>
  );
}

export function usePortalProject(): PortalProjectContextValue {
  const ctx = useContext(PortalProjectContext);
  if (!ctx) {
    throw new Error('usePortalProject must be used within PortalProjectProvider');
  }
  return ctx;
}

/** Safe outside provider — falls back to cookie/session getters. */
export function usePortalProjectOptional(): PortalProjectContextValue | null {
  return useContext(PortalProjectContext);
}
